import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  AuthError,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../config/firebase';
import { verifyEmail, verifyPasswordResetCode as verifyPasswordResetCodeApi, resetPassword as resetPasswordApi, recoverEmail } from '../utils/authApi';
import { BRAND } from '../brand';
import { Eye, EyeOff } from 'lucide-react';

type ActionMode = 'verifyEmail' | 'resetPassword' | 'recoverEmail';
type ActionStatus = 'loading' | 'processing' | 'resetForm' | 'success' | 'error';

const AuthActionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ActionStatus>('loading');
  const [message, setMessage] = useState<string>('Processing...');
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ActionMode | null>(null);
  const [actionCode, setActionCode] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  
  // Password reset form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleEmailVerification = useCallback(async (code: string) => {
    try {
      // Verify action code using Firebase client SDK
      await applyActionCode(auth!, code);
      
      // Get user info after verification
      let userEmail: string | undefined;
      let firebaseUid: string | undefined;
      
      if (auth?.currentUser) {
        await auth.currentUser.reload();
        userEmail = auth.currentUser.email || undefined;
        firebaseUid = auth.currentUser.uid;
      } else {
        // Try to get user from auth state
        await new Promise<void>((resolve) => {
          const unsubscribe = onAuthStateChanged(auth!, async (user: User | null) => {
            unsubscribe();
            if (user) {
              try {
                await user.reload();
                userEmail = user.email || undefined;
                firebaseUid = user.uid;
              } catch {
                // Continue
              }
            }
            resolve();
          });
          
          setTimeout(() => {
            unsubscribe();
            resolve();
          }, 1000);
        });
      }
      
      // Track verification on backend
      if (userEmail || firebaseUid) {
        try {
          await verifyEmail(code, userEmail, firebaseUid);
        } catch (trackingError) {
          // Log but don't fail - verification already succeeded
          console.error('Failed to track email verification:', trackingError);
        }
      }
      
      setStatus('success');
      setMessage('Your email has been successfully verified! You can now sign in to your account.');
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      const authError = err as AuthError;
      
      // Check if the error is because the code was already used
      if (authError.code === 'auth/invalid-action-code') {
        await new Promise(resolve => setTimeout(resolve, 300));
        
        if (!auth) {
          setStatus('error');
          setError('This verification link is invalid or has already been used.');
          return;
        }
        
        const authInstance = auth;
        let emailVerified = false;
        let userEmail: string | undefined;
        let firebaseUid: string | undefined;
        
        const currentUser = authInstance.currentUser;
        if (currentUser) {
          try {
            await currentUser.reload();
            emailVerified = currentUser.emailVerified;
            userEmail = currentUser.email || undefined;
            firebaseUid = currentUser.uid;
          } catch {
            // Continue to check via listener
          }
        }
        
        if (!emailVerified) {
          const result = await new Promise<{ verified: boolean; email?: string; uid?: string }>((resolve) => {
            const unsubscribe = onAuthStateChanged(authInstance, async (user: User | null) => {
              unsubscribe();
              if (user) {
                try {
                  await user.reload();
                  resolve({
                    verified: user.emailVerified,
                    email: user.email || undefined,
                    uid: user.uid
                  });
                } catch {
                  resolve({ verified: false });
                }
              } else {
                resolve({ verified: false });
              }
            });
            
            setTimeout(() => {
              unsubscribe();
              resolve({ verified: false });
            }, 1000);
          });
          
          emailVerified = result.verified;
          if (result.email) userEmail = result.email;
          if (result.uid) firebaseUid = result.uid;
        }
        
        if (emailVerified) {
          // Track already verified event on backend
          if (userEmail || firebaseUid) {
            try {
              await verifyEmail(code, userEmail, firebaseUid);
            } catch (trackingError) {
              console.error('Failed to track email verification:', trackingError);
            }
          }
          
          setStatus('success');
          setMessage('Your email is already verified! You can now sign in to your account.');
          setTimeout(() => {
            navigate('/login');
          }, 3000);
          return;
        }
        
        setStatus('error');
        setError('This verification link is invalid or has already been used.');
        return;
      }
      
      setStatus('error');
      switch (authError.code) {
        case 'auth/expired-action-code':
          setError('This verification link has expired. Please request a new verification email.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled. Please contact support.');
          break;
        case 'auth/user-not-found':
          setError('No account found for this verification link.');
          break;
        default:
          setError(`Verification failed: ${authError.message || 'An unknown error occurred.'}`);
      }
    }
  }, [navigate]);

  const handlePasswordReset = useCallback(async (code: string) => {
    try {
      // Verify the password reset code and get the email
      const emailFromCode = await verifyPasswordResetCode(auth!, code);
      setEmail(emailFromCode);
      
      // Track password reset initiation on backend
      try {
        await verifyPasswordResetCodeApi(code, emailFromCode);
      } catch (trackingError) {
        // Log but don't fail - verification already succeeded
        console.error('Failed to track password reset initiation:', trackingError);
      }
      
      setStatus('resetForm');
      setMessage('Please enter your new password.');
    } catch (err: unknown) {
      const authError = err as AuthError;
      setStatus('error');
      switch (authError.code) {
        case 'auth/expired-action-code':
          setError('This password reset link has expired. Please request a new password reset email.');
          break;
        case 'auth/invalid-action-code':
          setError('This password reset link is invalid or has already been used.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled. Please contact support.');
          break;
        case 'auth/user-not-found':
          setError('No account found for this password reset link.');
          break;
        default:
          setError(`Password reset failed: ${authError.message || 'An unknown error occurred.'}`);
      }
    }
  }, []);

  const handleEmailRecovery = useCallback(async (code: string) => {
    try {
      // Verify action code using Firebase client SDK
      await applyActionCode(auth!, code);
      
      // Get user info after recovery
      let userEmail: string | undefined;
      let firebaseUid: string | undefined;
      
      if (auth?.currentUser) {
        await auth.currentUser.reload();
        userEmail = auth.currentUser.email || undefined;
        firebaseUid = auth.currentUser.uid;
      } else {
        // Try to get user from auth state
        await new Promise<void>((resolve) => {
          const unsubscribe = onAuthStateChanged(auth!, async (user: User | null) => {
            unsubscribe();
            if (user) {
              try {
                await user.reload();
                userEmail = user.email || undefined;
                firebaseUid = user.uid;
              } catch {
                // Continue
              }
            }
            resolve();
          });
          
          setTimeout(() => {
            unsubscribe();
            resolve();
          }, 1000);
        });
      }
      
      // Track recovery on backend
      if (userEmail || firebaseUid) {
        try {
          await recoverEmail(code, userEmail, firebaseUid);
        } catch (trackingError) {
          // Log but don't fail - recovery already succeeded
          console.error('Failed to track email recovery:', trackingError);
        }
      }
      
      setStatus('success');
      setMessage('Your email address has been successfully reverted! You can now sign in with your previous email address.');
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      const authError = err as AuthError;
      setStatus('error');
      switch (authError.code) {
        case 'auth/expired-action-code':
          setError('This email recovery link has expired. Please contact support if you need assistance.');
          break;
        case 'auth/invalid-action-code':
          setError('This email recovery link is invalid or has already been used.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled. Please contact support.');
          break;
        default:
          setError(`Email recovery failed: ${authError.message || 'An unknown error occurred.'}`);
      }
    }
  }, [navigate]);

  useEffect(() => {
    const handleAction = async () => {
      // Check if Firebase is configured
      if (!isFirebaseConfigured() || !auth) {
        setStatus('error');
        setError('Firebase is not configured. Please contact support.');
        return;
      }

      // Get the action code and mode from URL parameters
      const urlMode = searchParams.get('mode') as ActionMode | null;
      const urlActionCode = searchParams.get('oobCode');

      if (!urlActionCode) {
        setStatus('error');
        setError('Invalid action link. The link may have expired or is malformed.');
        return;
      }

      if (!urlMode || !['verifyEmail', 'resetPassword', 'recoverEmail'].includes(urlMode)) {
        setStatus('error');
        setError('Invalid action mode. Please check your link and try again.');
        return;
      }

      setMode(urlMode);
      setActionCode(urlActionCode);
      setStatus('processing');

      // Handle different action modes
      if (urlMode === 'verifyEmail') {
        await handleEmailVerification(urlActionCode);
      } else if (urlMode === 'resetPassword') {
        await handlePasswordReset(urlActionCode);
      } else if (urlMode === 'recoverEmail') {
        await handleEmailRecovery(urlActionCode);
      }
    };

    handleAction();
  }, [searchParams, navigate, handleEmailVerification, handlePasswordReset, handleEmailRecovery]);

  const handlePasswordResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!actionCode) {
      setError('Invalid reset code. Please request a new password reset.');
      return;
    }

    setIsResettingPassword(true);

    try {
      // Confirm password reset using Firebase client SDK
      await confirmPasswordReset(auth!, actionCode, newPassword);
      
      // Track password reset completion on backend
      if (email) {
        try {
          await resetPasswordApi(actionCode, newPassword, email);
        } catch (trackingError) {
          // Log but don't fail - password reset already succeeded
          console.error('Failed to track password reset:', trackingError);
        }
      }
      
      setStatus('success');
      setMessage('Your password has been successfully reset! You can now sign in with your new password.');
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      const authError = err as AuthError;
      setError(null);
      setIsResettingPassword(false);
      
      switch (authError.code) {
        case 'auth/expired-action-code':
          setError('This password reset link has expired. Please request a new password reset email.');
          setStatus('error');
          break;
        case 'auth/invalid-action-code':
          setError('This password reset link is invalid or has already been used.');
          setStatus('error');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please use at least 6 characters.');
          break;
        case 'auth/password-does-not-meet-requirements':
          // Log the full error for debugging
          console.error('Firebase password requirements error:', authError.message);
          
          // Try to extract specific requirements from the error message
          let requirementsMessage = 'Password does not meet requirements. Please ensure your password contains at least 8 characters, including uppercase letters, lowercase letters, and numbers.';
          if (authError.message) {
            // Handle different error message formats - match content inside square brackets
            // Example: "Missing password requirements: [Password must contain a numeric character]"
            const requirementsMatch = authError.message.match(/\[(.*?)\]/);
            
            if (requirementsMatch && requirementsMatch[1]) {
              const requirementsList: string[] = [];
              const rawRequirements = requirementsMatch[1];
              
              // Log what we extracted for debugging
              console.log('Extracted requirements from error:', rawRequirements);
              
              // Split by comma if multiple requirements, otherwise use the whole string
              const individualRequirements = rawRequirements.split(',').map(r => r.trim());
              
              // Extract individual requirements and translate them to user-friendly messages
              for (const req of individualRequirements) {
                // Check for specific requirement patterns
                if (req.match(/numeric|number/i)) {
                  requirementsList.push('at least one number');
                } else if (req.match(/uppercase/i)) {
                  requirementsList.push('at least one uppercase letter');
                } else if (req.match(/lowercase/i)) {
                  requirementsList.push('at least one lowercase letter');
                } else if (req.match(/non-alphanumeric|special|symbol/i)) {
                  requirementsList.push('at least one special character');
                } else if (req.match(/length|characters? long|at least \d+/i)) {
                  const lengthMatch = req.match(/at least (\d+)|(\d+) character/i);
                  if (lengthMatch) {
                    const length = lengthMatch[1] || lengthMatch[2];
                    requirementsList.push(`at least ${length} characters long`);
                  }
                }
              }
              
              // Remove duplicates
              const uniqueRequirements = Array.from(new Set(requirementsList));
              
              if (uniqueRequirements.length > 0) {
                // Format requirements nicely
                let formattedRequirements = '';
                if (uniqueRequirements.length === 1) {
                  formattedRequirements = uniqueRequirements[0];
                } else if (uniqueRequirements.length === 2) {
                  formattedRequirements = uniqueRequirements.join(' and ');
                } else {
                  formattedRequirements = uniqueRequirements.slice(0, -1).join(', ') + ', and ' + uniqueRequirements[uniqueRequirements.length - 1];
                }
                
                // Note: Firebase may only show one missing requirement at a time
                // Even if this requirement seems to be met, check all common requirements
                requirementsMessage = `Password does not meet requirements. Your password must contain: ${formattedRequirements}. Please also ensure your password has at least 8 characters, including uppercase and lowercase letters.`;
              }
            }
          }
          setError(requirementsMessage);
          break;
        default:
          setError(`Password reset failed: ${authError.message || 'An unknown error occurred.'}`);
      }
    }
  };

  const getPageTitle = (): string => {
    switch (mode) {
      case 'verifyEmail':
        return 'Email Verification';
      case 'resetPassword':
        return 'Reset Password';
      case 'recoverEmail':
        return 'Recover Email';
      default:
        return 'Account Action';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center shadow-lg">
            <img src={BRAND.logoSrc} alt={BRAND.name} className="h-16 w-16" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
            {getPageTitle()}
          </h2>
        </div>

        {/* Status Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 space-y-6">
          {/* Loading State */}
          {(status === 'loading' || status === 'processing') && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <svg className="animate-spin h-12 w-12 text-green-600 dark:text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
            </div>
          )}

          {/* Password Reset Form */}
          {status === 'resetForm' && (
            <form onSubmit={handlePasswordResetSubmit} className="space-y-4">
              {email && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Resetting password for: <span className="font-medium text-gray-900 dark:text-gray-100">{email}</span>
                  </p>
                </div>
              )}
              
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="••••••••"
                    disabled={isResettingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                    tabIndex={-1}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    disabled={isResettingPassword}
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-new-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    id="confirm-new-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="••••••••"
                    disabled={isResettingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    disabled={isResettingPassword}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isResettingPassword}
                className="w-full py-2.5 px-4 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center"
              >
                {isResettingPassword ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Resetting Password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <svg className="h-12 w-12 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-green-700 dark:text-green-300 font-medium">{message}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Redirecting to login page...</p>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <svg className="h-12 w-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-700 dark:text-red-300 font-medium">{error || 'An error occurred.'}</p>
              <div className="pt-4">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                >
                  Go to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthActionPage;

